import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { SettingsStorage } from '@next/core-lib/settings';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { LayerContextKeys } from 'src/app/shared/constants';

import { ReferenceScheme, UpsertReferenceSchemeResponse } from '../models/reference-scheme.model';
import { ReferenceSchemeModule } from '../reference-scheme.module';
import * as referenceSchemeActions from './reference-scheme.actions';
import { getSelectedCountry } from './reference-scheme.selectors';
import { ProductService } from '../../product/services/product.service';
import { Product } from '../../shared/models';
import { SettingsServiceHelper } from '../../shared/services/settingsHelperService';
import { ReferenceSchemeService } from '../services/reference-scheme.service';

@Injectable()
export class ReferenceSchemeEffects {
  constructor(
    private actions$: Actions,
    private store: Store<ReferenceSchemeModule>,
    private referenceSchemeService: ReferenceSchemeService,
    private router: Router,
    private dialogService: DialogService,
    @Inject(LocaleService) private locale: LocaleService,
    private settingsService: SettingsServiceHelper,
    private ngZone: NgZone,
    private productService: ProductService,
  ) {}

  /* LOAD DATA EFFECTS */
  loadData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(referenceSchemeActions.loadData),
      concatMap((_) =>
        of(
          referenceSchemeActions.clearReferenceSchemeState(),
          referenceSchemeActions.loadSelectedCountryRequest(),
        ),
      ),
    ),
  );

  loadSelectedCountrySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(referenceSchemeActions.loadSelectedCountrySuccess),
      map(() => referenceSchemeActions.loadReferenceSchemesRequest()),
    ),
  );

  loadReferenceSchemesRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(referenceSchemeActions.loadReferenceSchemesRequest),
      withLatestFrom(this.store.select(getSelectedCountry)),
      filter(([_, country]) => country !== undefined),
      mergeMap(([_, country]) =>
        this.referenceSchemeService.getReferenceSchemes(country.toString()).pipe(
          switchMap((referenceSchemes: ReferenceScheme[]) =>
            of(
              referenceSchemeActions.loadReferenceSchemesSuccess({
                referenceSchemes,
              }),
              referenceSchemeActions.loadProductsByCountryRequest({ country: country.toString() }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              referenceSchemeActions.loadReferenceSchemesFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadSelectedCountry$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(referenceSchemeActions.loadSelectedCountryRequest),
      exhaustMap((_) =>
        this.settingsService
          .getSingle('BE', LayerContextKeys.REFERENCE_SCHEMES_COUNTRY, SettingsStorage.Global, true)
          .pipe(
            switchMap((settings) =>
              of(referenceSchemeActions.loadSelectedCountrySuccess({ id: settings })),
            ),
            catchError((error: HttpErrorResponse) =>
              of(
                referenceSchemeActions.loadReferenceSchemesFailed({ error }),
                NotificationActions.errorSnackbar({ message: error.message }),
              ),
            ),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  loadProductsByCountry$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(referenceSchemeActions.loadProductsByCountryRequest),
      exhaustMap((country) =>
        this.productService.getProducts().pipe(
          switchMap((products) =>
            of(
              referenceSchemeActions.loadProductsByCountrySuccess({
                products: (products as Product[]).filter(
                  (x) => x?.country === country.country.toString(),
                ),
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              referenceSchemeActions.loadProductsByCountryFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  setSelectedCountry$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(referenceSchemeActions.setSelectedCountryRequest),
      exhaustMap((id) =>
        this.referenceSchemeService.getReferenceSchemes(id.id).pipe(
          switchMap((referenceScheme: ReferenceScheme[]) => {
            this.settingsService.setSettings(
              LayerContextKeys.REFERENCE_SCHEMES_COUNTRY,
              id.id,
              SettingsStorage.Global,
              true,
            );

            return of(
              referenceSchemeActions.setSelectedCountrySuccess({
                id: id.id,
                referenceSchemes: referenceScheme.filter((x) => x.country === id.id),
              }),
            );
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              referenceSchemeActions.loadReferenceSchemesFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  /* NAVIGATION EFFECTS */
  createReferenceSchemeNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(referenceSchemeActions.createReferenceSchemeNavigation),
        tap(() => {
          this.router.navigate(['reference-scheme', 'create']);
        }),
      ),
    {
      dispatch: false,
    },
  );

  editReferenceSchemeNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(referenceSchemeActions.editReferenceSchemeNavigation),
        tap((action) => {
          this.router.navigate(['reference-scheme', 'edit', action.referenceScheme.id]);
        }),
      ),
    {
      dispatch: false,
    },
  );

  viewReferenceSchemeNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(referenceSchemeActions.viewReferenceSchemeNavigation),
        tap((action) =>
          this.router.navigate(['reference-scheme', 'view', action.referenceScheme.id]),
        ),
      ),
    {
      dispatch: false,
    },
  );

  /* UPSERT EFFECTS */
  upsertReferenceSchemeRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(referenceSchemeActions.upsertReferenceSchemeRequest),
      exhaustMap(({ referenceScheme }) =>
        this.referenceSchemeService.upsertReferenceScheme(referenceScheme).pipe(
          map((response: UpsertReferenceSchemeResponse) =>
            referenceSchemeActions.upsertReferenceSchemeSuccess({
              referenceScheme: {
                ...referenceScheme,
                id: response.id,
              },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              referenceSchemeActions.upsertReferenceSchemeFailed({ error }),
              referenceSchemeActions.loadReferenceSchemesRequest(),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
  upsertReferenceSchemeSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(referenceSchemeActions.upsertReferenceSchemeSuccess),
        tap((_) => {
          this.router.navigate(['reference-scheme']);
        }),
      ),
    {
      dispatch: false,
    },
  );

  /* DELETE EFFECTS */
  deleteReferenceSchemeWithConfirm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(referenceSchemeActions.deleteReferenceSchemeConfirm),
      switchMap((action) =>
        this.dialogService
          .confirmWarning({
            text: this.locale.translate(`reference-scheme.dialog.deleteConfirm`),
          })
          .pipe(
            switchMap(
              (result) =>
                (result.status === DIALOG_RETURN_TYPES.confirm && [
                  referenceSchemeActions.deleteReferenceSchemeRequest({
                    referenceSchemeId: action.referenceScheme.id,
                  }),
                ]) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  deleteReferenceSchemeRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(referenceSchemeActions.deleteReferenceSchemeRequest),
      mergeMap((action) =>
        this.referenceSchemeService.deleteReferenceScheme(action.referenceSchemeId).pipe(
          map(() =>
            referenceSchemeActions.deleteReferenceSchemeSuccess({
              referenceSchemeId: action.referenceSchemeId,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              referenceSchemeActions.deleteReferenceSchemeFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteReferenceSchemeSuccess$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(referenceSchemeActions.deleteReferenceSchemeSuccess),
        tap(() => {
          this.ngZone.run(() => this.router.navigate(['/reference-scheme'])).then();
        }),
      ),
    { dispatch: false },
  );
}
